/**
 * 수강권 구매(폼 2-B) React Query 훅
 *
 * 백엔드 `purchaseTicket` (packages/@vlab-corporation/firebase-functions/src/ticket/purchase-ticket.ts)와
 * 1:1 contract. 회원 상세 페이지에서 모달을 통해 호출한다.
 */

import { useMutation } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";

// --- 백엔드 contract ---

interface PurchaseTicketRequest {
  centerName: string;
  memberName: string;
  memberMobile: string;
  /** Step 7에서 발급된 카탈로그 ticketId (UUID v4) */
  ticketId: string;
}

interface PurchaseTicketResponse {
  success: true;
}

export interface PurchaseTicketParams {
  centerName: string;
  memberName: string;
  memberMobile: string;
  ticketId: string;
}

/**
 * 회원 보유 수강권 1건 (백엔드 `getMemberTickets` 응답 형태).
 *
 * top-level `members`는 클라이언트 직접 read 권한이 없어 백엔드 callable로 받는다.
 * `purchasedAt`은 서버에서 ISO string으로 직렬화된 값.
 */
export interface MemberTicket {
  title: string;
  type: "P" | "G";
  remainingCoupon: number;
  maxCoupon: number;
  isActive: boolean;
  /** ISO 8601 또는 정규화된 datetime string. 무제한 수강권은 부재. */
  expireAt?: string;
  /** ISO/정규화 datetime string. Studiomate 동기화본은 구매일 정보가 없어 부재할 수 있다. */
  purchasedAt?: string;
}

interface GetMemberTicketsRequest {
  centerId: number;
  memberId: number;
}

interface GetMemberTicketsResponse {
  tickets: MemberTicket[];
}

/**
 * 회원 보유 수강권을 백엔드 callable로 조회한다.
 *
 * 격리: 백엔드가 호출자 소유 센터(`users/{uid}/centers/{centerId}/members/{memberId}`)
 * 존재로 권한을 검증하므로, 본인 센터 회원만 조회된다.
 */
export async function getMemberTickets(
  centerId: number,
  memberId: number,
): Promise<MemberTicket[]> {
  const fn = httpsCallable<GetMemberTicketsRequest, GetMemberTicketsResponse>(
    getFirebaseFunctions(),
    "getMemberTickets",
  );
  const result = await fn({ centerId, memberId });
  return result.data.tickets;
}

/**
 * 수강권 구매 mutation.
 *
 * 성공 시 호출자(모달의 onSuccess)에서 회원 데이터를 refetch해 tickets[]
 * 변경분을 반영한다. 본 훅은 cache 무효화를 직접 트리거하지 않는다.
 */
export function usePurchaseTicket() {
  return useMutation({
    mutationFn: async (params: PurchaseTicketParams) => {
      const fn = httpsCallable<PurchaseTicketRequest, PurchaseTicketResponse>(
        getFirebaseFunctions(),
        "purchaseTicket",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

// --- 공통: 수강권 식별 contract (순번 + 정합성 가드) ---

/**
 * 수강권 1건을 식별하는 공통 파라미터.
 *
 * `tickets[]`에 안정적 id가 없어, UI가 본 직렬화 배열 순번(ticketIndex)과
 * title/purchasedAt을 함께 보내고 백엔드가 동일 직렬화로 순번→raw 인덱스를
 * 매핑 + 정합성 가드를 수행한다. expire/update/delete 모두 동일.
 */
export interface MemberTicketIdentity {
  centerId: number;
  memberId: number;
  ticketIndex: number;
  expectedTitle: string;
  expectedPurchasedAt?: string;
}

interface MutationSuccessResponse {
  success: true;
}

// --- 수강권 만료(소프트 비활성) contract (expire-member-ticket.ts) ---

/**
 * 회원 보유 수강권 1건을 소프트 만료(isActive:false)한다. 물리 제거하지 않아
 * 구매·사용 이력이 보존된다(완전 제거는 useDeleteMemberTicket).
 * 성공 시 호출자가 tickets를 refetch한다.
 */
export function useExpireMemberTicket() {
  return useMutation({
    mutationFn: async (params: MemberTicketIdentity) => {
      const fn = httpsCallable<MemberTicketIdentity, MutationSuccessResponse>(
        getFirebaseFunctions(),
        "expireMemberTicket",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

// --- 수강권 수정(운영 보정) contract (update-member-ticket.ts) ---

export interface UpdateMemberTicketParams extends MemberTicketIdentity {
  /** 남은 횟수 (0 이상 정수) */
  remainingCoupon: number;
  /** 총 횟수 (1 이상 정수) */
  maxCoupon: number;
  /** 만료일. ISO 문자열이면 설정, null이면 제거(무제한) */
  expireAt: string | null;
  /** 구매일. ISO 문자열이면 설정, null이면 제거(구매일 없음) */
  purchasedAt: string | null;
}

/**
 * 회원 보유 수강권 1건의 운영 보정 필드(남은 횟수·총 횟수·만료일)를 수정한다.
 * title/type/isActive는 변경하지 않는다. 성공 시 호출자가 tickets를 refetch한다.
 */
export function useUpdateMemberTicket() {
  return useMutation({
    mutationFn: async (params: UpdateMemberTicketParams) => {
      const fn = httpsCallable<UpdateMemberTicketParams, MutationSuccessResponse>(
        getFirebaseFunctions(),
        "updateMemberTicket",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

// --- 수강권 물리 삭제(하드) contract (delete-member-ticket.ts) ---

/**
 * 회원 보유 수강권 1건을 배열에서 완전히 제거한다(이력 보존 안 됨).
 * 성공 시 호출자가 tickets를 refetch한다.
 */
export function useDeleteMemberTicket() {
  return useMutation({
    mutationFn: async (params: MemberTicketIdentity) => {
      const fn = httpsCallable<MemberTicketIdentity, MutationSuccessResponse>(
        getFirebaseFunctions(),
        "deleteMemberTicket",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}
